import React, { createContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import type {
  Control,
  FieldErrors,
  Resolver,
  UseFormClearErrors,
  UseFormResetField,
} from 'react-hook-form';

import Heading from 'components/partials/heading/heading';
import FlashNotification from 'components/partials/flash-notification/flash-notification';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Loader from 'components/partials/loader/loader';
import ActionMenu from 'components/partials/action-menu/action-menu';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { getStatusLabel } from 'components/partials/table-component/helpers/get-status-label';

import { useCurrentUser } from 'hooks/api/use-current-user';
import {
  useCreateOrUpdateUser,
  usePartialUpdateUserMutation,
} from 'hooks/api/use-users';
import { useImpersonation } from 'hooks/use-impersonation';
import { useReadOnly } from 'hooks/read-only';
import { useCanOnlyEditJobTitle } from 'hooks/use-can-only-edit-job-title';

import UserForm from 'containers/admin/users/user/forms/user-form';
import {
  onUserFormSubmit,
  getBreadcrumbsForUserPage,
  getRedirectPathForUser,
} from 'containers/admin/users/user/user-profile/user-profile.helpers';
import {
  USER_PROFILE_FORM_VALIDATION_SCHEMA,
  USERS_PROFILE_STATUSES,
} from 'containers/admin/users/user/user-profile/user-profile.constants';

import type { UserActionMenuItem } from 'containers/admin/users/user/user-action-menu-items';

const CAMPUS_USER = 'Campus User';
const INTERNAL_USER = 'Internal User';

export interface UserProfileContextValue {
  control: Control<Record<string, unknown>> | Record<string, unknown>;
  errors: FieldErrors;
  clearErrors: UseFormClearErrors<Record<string, unknown>> | (() => void);
  resetField: UseFormResetField<Record<string, unknown>> | (() => void);
  canOnlyEditJobTitle: boolean;
}

export const UserProfileContext = createContext<UserProfileContextValue>({
  control: {},
  errors: {},
  clearErrors: () => {},
  resetField: () => {},
  canOnlyEditJobTitle: false,
});

interface UserLike {
  id?: string | number;
  authority?: string;
  name?: string;
  status?: string;
  client?: { id?: string | number; [key: string]: unknown } | null;
  [key: string]: unknown;
}

interface ClientLike {
  id?: string | number;
  name?: string;
  [key: string]: unknown;
}

interface DefaultUserFormValues {
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  email?: string;
  confirmEmail?: string;
  role?: string;
  client?: { value?: string | number; label?: string } | null;
  [key: string]: unknown;
}

interface UserProfileProps {
  pageName?: string;
  currentClient?: ClientLike | null;
  user?: UserLike | null;
  isCurrentUserProfile?: boolean;
  isFormDisabled?: boolean;
  setIsFormActive?: (active: boolean) => void;
  loading?: boolean;
  actionMenuItems?: UserActionMenuItem[] | false;
  isImpersonatingUser?: boolean;
  reloadUser: () => void;
  defaultValues?: DefaultUserFormValues;
}

const UserProfile = ({
  pageName = 'Add new user',
  currentClient,
  user,
  isCurrentUserProfile,
  isFormDisabled = false,
  setIsFormActive,
  loading,
  actionMenuItems = [],
  isImpersonatingUser,
  reloadUser,
  defaultValues = {
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    confirmEmail: '',
    role: 'user',
    client: null,
  },
}: UserProfileProps): React.JSX.Element => {
  const [loader, setLoader] = useState(false);
  const [isNotNewUser, setIsNotNewUser] = useState(false);
  const [redirectPath, setRedirectPath] = useState('');

  const currentUser = useCurrentUser();
  const createOrUpdateUser = useCreateOrUpdateUser();
  const partialUpdateUser = usePartialUpdateUserMutation();

  const navigate = useNavigate();
  const location = useLocation();
  const readOnly = useReadOnly();

  const impersonation = useImpersonation();
  const canOnlyEditJobTitle = useCanOnlyEditJobTitle({
    user,
    isCurrentUserProfile,
  });

  const isUserPage = location.pathname.includes('/admin/users');

  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    resetField,
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultValues as Record<string, unknown>,
    resolver: USER_PROFILE_FORM_VALIDATION_SCHEMA as unknown as Resolver<
      Record<string, unknown>
    >,
  });

  const isMVUser =
    (readOnly && defaultValues?.role !== CAMPUS_USER) ||
    (readOnly && defaultValues?.role !== INTERNAL_USER);

  const isEditButtonVisible =
    !impersonation.isImpersonating() &&
    isNotNewUser &&
    (isCurrentUserProfile || !readOnly || canOnlyEditJobTitle);

  const getUserLabel = (): React.ReactNode => {
    if (user) {
      return getStatusLabel(
        user?.status === 'ACTIVE' ? 'Active' : 'Locked',
        USERS_PROFILE_STATUSES
      );
    }

    return null;
  };

  const onCancel = (): void => {
    if (!user) {
      navigate(redirectPath);
    } else {
      reset(defaultValues as Record<string, unknown>);
      if (setIsFormActive) setIsFormActive(true);
    }
  };

  const onSubmit = (formData: Record<string, unknown>): void => {
    setLoader(true);

    onUserFormSubmit({
      formData: formData as never,
      navigate,
      redirectPath,
      users: {
        createOrUpdate: (data: Record<string, unknown>) =>
          canOnlyEditJobTitle
            ? partialUpdateUser.mutateAsync({
                id: user?.id as string | number,
                jobTitle: (data.jobTitle as string | undefined) ?? '',
              })
            : createOrUpdateUser.mutateAsync(data),
      } as never,
      user: user ?? null,
      currentUser: currentUser as never,
      isCurrentUserProfile,
      reloadUser,
    })
      .catch(() => undefined)
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    setIsNotNewUser(!!user);
    setRedirectPath(
      isCurrentUserProfile
        ? '/admin/profile'
        : getRedirectPathForUser({ setIsFormActive, user, currentClient })
    );
  }, [user, setIsFormActive]);

  useEffect(() => {
    reset(defaultValues as Record<string, unknown>);
  }, [user]);

  return loading ? (
    <Loader {...{ loading: loading }} />
  ) : (
    <UserProfileContext.Provider
      value={{
        control: control as Control<Record<string, unknown>>,
        errors,
        clearErrors,
        resetField,
        canOnlyEditJobTitle,
      }}
    >
      <ResponsiveWrapper>
        <Loader {...{ loading: loader }} />

        {!isCurrentUserProfile && (
          <Breadcrumbs
            breadcrumbs={
              getBreadcrumbsForUserPage(
                isUserPage,
                currentClient,
                user
              ) as never
            }
          />
        )}
        <div className="flex items-center justify-between mt-4">
          <Heading
            variant={isCurrentUserProfile ? 'h2' : 'h1'}
            data-testid="user-profile-heading"
          >
            {pageName}
          </Heading>

          {isEditButtonVisible && (
            <ActionMenu
              options={(!canOnlyEditJobTitle && actionMenuItems) || []}
              onEditClick={() => setIsFormActive && setIsFormActive(false)}
              isActionMenuDisabled={!isFormDisabled}
            />
          )}
        </div>

        {!isMVUser && !isCurrentUserProfile ? (
          <div data-testid="user-status-label">{getUserLabel()}</div>
        ) : null}

        <FlashNotification customClass="mb-4 mt-0" />

        <UserForm
          {...{
            isMVUser,
            userRole: defaultValues?.role as string | undefined,
            currentClient: currentClient as never,
            isFormDisabled,
            isSelectDisabled: !!(
              !!currentClient ||
              isImpersonatingUser ||
              currentUser?.data?.basicUser ||
              defaultValues?.role === CAMPUS_USER ||
              defaultValues?.role === INTERNAL_USER ||
              isMVUser
            ),
            isNotNewUser,
            isCurrentUserProfile,
            onCancel,
            onSubmit,
            handleSubmit,
          }}
        />
      </ResponsiveWrapper>
    </UserProfileContext.Provider>
  );
};

export default UserProfile;
